import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sessionsApi, speakersApi, agendaApi } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Trash2, Edit, Plus, X } from "lucide-react";

const GRADE_BANDS = [
  { value: "prek", label: "Pre K" },
  { value: "k2", label: "K-2" },
  { value: "35", label: "3-5" },
  { value: "68", label: "6-8" },
  { value: "912", label: "9-12" },
];

const AGENDA_TYPES = ["general", "session", "break", "lunch", "keynote"];

function SessionForm({ initial, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    presenter: initial?.presenter || "",
    location: initial?.location || "",
    time_slot: initial?.time_slot || "",
    grade_band: initial?.grade_band || "prek",
    sort_order: initial?.sort_order || 0,
  });
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, sort_order: Number(form.sort_order) || 0 });
      }}
      className="space-y-3 bg-muted/40 p-4 rounded-lg border"
      data-testid="session-form"
    >
      <div>
        <Label>Title *</Label>
        <Input
          required
          value={form.title}
          onChange={set("title")}
          data-testid="session-title-input"
        />
      </div>
      <div>
        <Label>Description (Markdown supported)</Label>
        <Textarea
          rows={3}
          value={form.description}
          onChange={set("description")}
          data-testid="session-description-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Presenter</Label>
          <Input value={form.presenter} onChange={set("presenter")} />
        </div>
        <div>
          <Label>Location</Label>
          <Input value={form.location} onChange={set("location")} />
        </div>
        <div>
          <Label>Time Slot</Label>
          <Input
            placeholder="9:00 AM - 10:00 AM"
            value={form.time_slot}
            onChange={set("time_slot")}
          />
        </div>
        <div>
          <Label>Grade Band</Label>
          <Select value={form.grade_band} onValueChange={set("grade_band")}>
            <SelectTrigger data-testid="session-grade-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRADE_BANDS.map((g) => (
                <SelectItem key={g.value} value={g.value}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Sort Order</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={set("sort_order")}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" data-testid="session-save-btn">
          Save
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function SpeakerForm({ initial, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    title: initial?.title || "",
    bio: initial?.bio || "",
    photo_url: initial?.photo_url || "",
    session: initial?.session || "",
    sort_order: initial?.sort_order || 0,
  });
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, sort_order: Number(form.sort_order) || 0 });
      }}
      className="space-y-3 bg-muted/40 p-4 rounded-lg border"
      data-testid="speaker-form"
    >
      <div>
        <Label>Name *</Label>
        <Input required value={form.name} onChange={set("name")} data-testid="speaker-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Title / Role</Label>
          <Input value={form.title} onChange={set("title")} />
        </div>
        <div>
          <Label>Session</Label>
          <Input value={form.session} onChange={set("session")} />
        </div>
      </div>
      <div>
        <Label>Photo URL</Label>
        <Input value={form.photo_url} onChange={set("photo_url")} />
      </div>
      <div>
        <Label>Bio</Label>
        <Textarea rows={3} value={form.bio} onChange={set("bio")} />
      </div>
      <div>
        <Label>Sort Order</Label>
        <Input
          type="number"
          value={form.sort_order}
          onChange={set("sort_order")}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" data-testid="speaker-save-btn">Save</Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function AgendaForm({ initial, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    start_time: initial?.start_time || "",
    end_time: initial?.end_time || "",
    location: initial?.location || "",
    description: initial?.description || "",
    type: initial?.type || "general",
    grade_band: initial?.grade_band || "prek",
    sort_order: initial?.sort_order || 0,
  });
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, sort_order: Number(form.sort_order) || 0 });
      }}
      className="space-y-3 bg-muted/40 p-4 rounded-lg border"
      data-testid="agenda-form"
    >
      <div>
        <Label>Title *</Label>
        <Input required value={form.title} onChange={set("title")} data-testid="agenda-title-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Start Time *</Label>
          <Input
            required
            placeholder="8:00 AM"
            value={form.start_time}
            onChange={set("start_time")}
          />
        </div>
        <div>
          <Label>End Time *</Label>
          <Input
            required
            placeholder="9:00 AM"
            value={form.end_time}
            onChange={set("end_time")}
          />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.type} onValueChange={set("type")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {AGENDA_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Grade Band</Label>
          <Select value={form.grade_band} onValueChange={set("grade_band")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRADE_BANDS.map((g) => (
                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Location</Label>
          <Input value={form.location} onChange={set("location")} />
        </div>
        <div>
          <Label>Sort Order</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={set("sort_order")}
          />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={form.description} onChange={set("description")} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" data-testid="agenda-save-btn">Save</Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function ResourceManager({ name, api, queryKey, FormComponent, renderRow }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: () => api.list(),
  });

  const createMut = useMutation({
    mutationFn: api.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      setShowForm(false);
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      setEditing(null);
    },
  });
  const deleteMut = useMutation({
    mutationFn: api.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">{name}</h2>
        {!showForm && !editing && (
          <Button
            onClick={() => setShowForm(true)}
            data-testid={`add-${queryKey}-btn`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        )}
      </div>

      {showForm && (
        <FormComponent
          onSubmit={(data) => createMut.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editing && (
        <FormComponent
          initial={editing}
          onSubmit={(data) => updateMut.mutate({ id: editing.id, data })}
          onCancel={() => setEditing(null)}
        />
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No items yet.</p>
      ) : (
        <div className="space-y-2" data-testid={`${queryKey}-list`}>
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-card border border-border rounded-lg p-3 flex items-start justify-between gap-3"
            >
              <div className="flex-1 min-w-0">{renderRow(item)}</div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(item)}
                  data-testid={`edit-${queryKey}-btn`}
                >
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm(`Delete this ${name.slice(0, -1)}?`))
                      deleteMut.mutate(item.id);
                  }}
                  data-testid={`delete-${queryKey}-btn`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid="admin-page">
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold text-foreground">
          Admin
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage Sessions, Speakers, and Agenda Items.
        </p>
      </div>

      <Tabs defaultValue="sessions">
        <TabsList className="mb-4">
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="speakers" data-testid="tab-speakers">Speakers</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Agenda Items</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <ResourceManager
            name="Sessions"
            api={sessionsApi}
            queryKey="sessions"
            FormComponent={SessionForm}
            renderRow={(s) => (
              <div>
                <p className="font-semibold text-sm">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {[
                    s.grade_band &&
                      GRADE_BANDS.find((g) => g.value === s.grade_band)?.label,
                    s.presenter,
                    s.location,
                    s.time_slot,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="speakers">
          <ResourceManager
            name="Speakers"
            api={speakersApi}
            queryKey="speakers"
            FormComponent={SpeakerForm}
            renderRow={(s) => (
              <div>
                <p className="font-semibold text-sm">{s.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {[s.title, s.session].filter(Boolean).join(" · ")}
                </p>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="agenda">
          <ResourceManager
            name="Agenda Items"
            api={agendaApi}
            queryKey="agenda-items"
            FormComponent={AgendaForm}
            renderRow={(a) => (
              <div>
                <p className="font-semibold text-sm">{a.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {a.start_time} - {a.end_time}
                  {a.grade_band &&
                    ` · ${GRADE_BANDS.find((g) => g.value === a.grade_band)?.label || ""}`}
                  {a.type && ` · ${a.type}`}
                </p>
              </div>
            )}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
